import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Building2, HelpCircle, Zap, ExternalLink, Upload, CheckCircle2, AlertCircle, FileKey, Radio, Info, Copy } from "lucide-react";
import { toast } from "sonner";
import { formatEnvironmentLabel } from "@/lib/labels";

const emptyForm = {
  bank_code: "077", banco_nome: "Banco Inter", agencia: "", conta: "",
  carteira: "", variacao_carteira: "", convenio: "", client_id: "", client_secret: "",
  certificado: "", chave_privada: "", webhook_url: "",
  pix_monitoring_url: "", pix_oauth_url: "", pix_webhook_url: "", pix_oauth_scope: "",
  ambiente: "sandbox", ativo: true,
  oauth_url: "", api_base_url: "", oauth_scope: "",
  extra_headers: {},
  hub_nosso_numero_inicio: 9000000001,
  hub_nosso_numero_tamanho: 10,
  cert_info: {},
};

export default function Bancos() {
  const { tenant } = useAuth();
  const [items, setItems] = useState([]);
  const [supported, setSupported] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const modules = tenant?.modules || {};
  const pixModuleEnabled = modules.controlemeupix === true;
  const suggestedPixWebhookUrl = tenant?.id && form.bank_code
    ? `${window.location.origin}/api/webhooks/bank/${tenant.id}/${form.bank_code}`
    : "";

  const load = async () => {
    const [b, s] = await Promise.all([api.get("/bancos"), api.get("/bancos-suportados")]);
    setItems(b.data); setSupported(s.data);
  };
  useEffect(() => { load(); }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const updateExtra = (k) => (e) => setForm((f) => ({
    ...f,
    extra_headers: { ...(f.extra_headers || {}), [k]: e.target.value },
  }));

  const applyPreset = async (code, { force = false } = {}) => {
    const meta = supported.find((b) => b.code === code);
    let preset = {};
    try {
      const { data } = await api.get(`/bancos-presets/${code}`);
      preset = data || {};
    } catch (_) { /* sem preset cadastrado */ }
    setForm((f) => {
      const hml = f.ambiente === "homologacao";
      const oauthPreset = hml ? (preset.auth_url_hml || preset.oauth_url_hml || preset.oauth_url || "") : (preset.oauth_url || "");
      const apiPreset = hml ? (preset.api_base_url_hml || preset.api_base_url || "") : (preset.api_base_url || "");
      const pixOauthPreset = hml ? (preset.pix_oauth_url_hml || preset.pix_oauth_url || oauthPreset || "") : (preset.pix_oauth_url || preset.oauth_url || "");
      const pixBasePreset = hml ? (preset.pix_monitoring_url_hml || preset.pix_monitoring_url || "") : (preset.pix_monitoring_url || "");
      return {
        ...f,
        bank_code: code,
        banco_nome: meta?.name || f.banco_nome || "",
        oauth_url: force ? oauthPreset : (f.oauth_url || oauthPreset),
        api_base_url: force ? apiPreset : (f.api_base_url || apiPreset),
        oauth_scope: force ? (preset.oauth_scope || "") : (f.oauth_scope || preset.oauth_scope || ""),
        pix_monitoring_url: force ? pixBasePreset : (f.pix_monitoring_url || pixBasePreset),
        pix_oauth_url: force ? pixOauthPreset : (f.pix_oauth_url || pixOauthPreset),
        pix_oauth_scope: force ? (preset.pix_oauth_scope || "") : (f.pix_oauth_scope || preset.pix_oauth_scope || ""),
        pix_webhook_url: force
          ? (tenant?.id ? `${window.location.origin}/api/webhooks/bank/${tenant.id}/${code}` : "")
          : (f.pix_webhook_url || (tenant?.id ? `${window.location.origin}/api/webhooks/bank/${tenant.id}/${code}` : "")),
      };
    });
  };

  const applyTemplate = async (code) => {
    if (!code || code === "NONE") {
      setSelectedTemplate("");
      return;
    }
    await applyPreset(code, { force: true });
    setSelectedTemplate(code);
    const meta = supported.find((b) => b.code === code);
    toast.success(`Template aplicado: ${meta?.name || code}`);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/bancos/${editId}`, form);
      else await api.post("/bancos", form);
      toast.success(editId ? "Banco atualizado" : "Banco cadastrado");
      setOpen(false); setForm(emptyForm); setEditId(null); setSelectedTemplate(""); load();
      window.dispatchEvent(new Event("up2:bancos-changed"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    }
  };

  const onSelectBank = async (code) => {
    setSelectedTemplate("");
    await applyPreset(code);
  };

  // PFX upload
  const [pfxBusy, setPfxBusy] = useState(false);
  const [pfxPwd, setPfxPwd] = useState("");
  const onPfxChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!pfxPwd) {
      toast.error("Digite a senha do certificado antes de selecionar o arquivo .pfx");
      e.target.value = "";
      return;
    }
    setPfxBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("password", pfxPwd);
      const { data } = await api.post("/bancos/extract-pfx", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({
        ...f,
        certificado: data.certificado_pem,
        chave_privada: data.chave_privada_pem,
        cert_info: data.info,
      }));
      toast.success(`Certificado extraído: ${data.info.common_name || "OK"} · expira em ${data.info.days_to_expire} dias`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao extrair certificado");
    } finally {
      setPfxBusy(false);
      setPfxPwd("");
      e.target.value = "";
    }
  };

  const edit = (b) => {
    setForm({ ...emptyForm, ...b, client_secret: "", chave_privada: "", certificado: "" });
    setSelectedTemplate("");
    setEditId(b.id); setOpen(true);
  };
  const del = (b) => {
  toast(`Excluir banco ${b.banco_nome}?`, {
    description: "Essa ação não poderá ser desfeita.",
    duration: Infinity,
    action: {
      label: "Excluir",
      onClick: async () => {
        try {
          await api.delete(`/bancos/${b.id}`);
          toast.success("Banco excluído com sucesso");
          load();
          window.dispatchEvent(new Event("up2:bancos-changed"));
        } catch {
          toast.error("Erro ao excluir o banco");
        }
      },
    },
    cancel: { label: "Cancelar" },
  });
};

  // Onboarding + test
  const [onboard, setOnboard] = useState(null);
  const [testDebug, setTestDebug] = useState(null);
  const onOnboard = async (b) => {
    const { data } = await api.get(`/onboarding/${b.bank_code}`);
    setOnboard({ ...data, banco_id: b.id });
  };
  const testar = async (b) => {
    toast.loading("Testando conexão...", { id: "test" });
    try {
      const { data } = await api.post(`/bancos/${b.id}/testar`);
      toast.dismiss("test");
      if (data.ok) toast.success(data.message);
      else {
        toast.error(data.message);
        if (data.diagnostics) setTestDebug({ message: data.message, diagnostics: data.diagnostics });
      }
    } catch (err) {
      toast.dismiss("test");
      const data = err?.response?.data || {};
      const message = data?.message || data?.detail || "Falha no teste";
      toast.error(message);
      if (data?.diagnostics) setTestDebug({ message, diagnostics: data.diagnostics });
    }
  };

  return (
    <div className="space-y-6" data-testid="bancos-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Conexões</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">Bancos</h1>
          <p className="text-sm text-zinc-500 mt-1">Credenciais e certificados por banco (multi-banco).</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(emptyForm); setEditId(null); setSelectedTemplate(""); } }}>
          <DialogTrigger asChild>
            <Button className="rounded-sm" data-testid="banco-new-button"><Plus className="w-4 h-4 mr-2" /> Novo banco</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto rounded-sm p-4 sm:p-6">
            <DialogHeader>
              <DialogTitle className="font-display">{editId ? "Editar banco" : "Novo banco"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2 border border-blue-200 rounded-sm p-3 bg-blue-50/70" data-testid="banco-template-block">
                <div className="flex items-center gap-2 mb-2">
                  <Info className="w-4 h-4 text-blue-700 shrink-0" />
                  <Label className="text-[10px] uppercase tracking-wider font-semibold text-blue-800">Usar template pré-configurado (opcional)</Label>
                </div>
                <Select value={selectedTemplate} onValueChange={applyTemplate}>
                  <SelectTrigger className="rounded-sm h-10 bg-white" data-testid="banco-template-select">
                    <SelectValue placeholder="Selecione um banco conhecido..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NONE">Nenhum (preencher manualmente)</SelectItem>
                    {supported.map((b) => (
                      <SelectItem key={b.code} value={b.code}>{b.code} - {b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-blue-700 mt-2">
                  O template preenche URLs de cobrança e de monitoramento Pix. Complete apenas credenciais, certificados, agência, conta e convênio.
                </p>
              </div>

              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">Banco</Label>
                <Select value={form.bank_code} onValueChange={onSelectBank}>
                  <SelectTrigger className="rounded-sm h-10" data-testid="banco-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {supported.map((b) => (
                      <SelectItem key={b.code} value={b.code}>{b.code} – {b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Field label="Agência" required value={form.agencia} onChange={update("agencia")} mono testid="banco-agencia" />
              <Field label="Conta" required value={form.conta} onChange={update("conta")} mono testid="banco-conta" />
              <Field label="Carteira" value={form.carteira} onChange={update("carteira")} mono testid="banco-carteira" />
              <Field label="Variação da carteira" value={form.variacao_carteira || ""} onChange={update("variacao_carteira")} mono testid="banco-variacao-carteira" />
              <Field label="Convênio" value={form.convenio} onChange={update("convenio")} mono testid="banco-convenio" />
              <Field
                label="Início nosso nº Hub"
                required
                type="number"
                min="1"
                value={form.hub_nosso_numero_inicio}
                onChange={update("hub_nosso_numero_inicio")}
                mono
                testid="banco-hub-nn-inicio"
              />
              <Field
                label="Tamanho nosso nº"
                required
                type="number"
                min="1"
                max="20"
                value={form.hub_nosso_numero_tamanho}
                onChange={update("hub_nosso_numero_tamanho")}
                mono
                testid="banco-hub-nn-tamanho"
              />
              <Field label="Client ID" value={form.client_id} onChange={update("client_id")} mono testid="banco-clientid" />
              <Field label="Client Secret" value={form.client_secret} onChange={update("client_secret")} mono type="password" testid="banco-clientsecret" />

              {/* PFX UPLOAD BLOCK */}
              <div className="sm:col-span-2 border border-zinc-200 rounded-sm p-3 sm:p-4 bg-zinc-50" data-testid="pfx-block">
                <div className="flex items-center gap-2 mb-3">
                  <FileKey className="w-4 h-4 text-zinc-600 shrink-0" />
                  <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-600">Importar certificado A1 (.pfx / .p12)</span>
                </div>
                <p className="text-xs text-zinc-500 mb-3">Suba seu arquivo .pfx e digite a senha — o certificado e a chave privada serão extraídos automaticamente.</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <Input
                    type="password"
                    placeholder="Senha do certificado"
                    value={pfxPwd}
                    onChange={(e) => setPfxPwd(e.target.value)}
                    className="rounded-sm h-10 font-mono text-xs sm:col-span-2"
                    data-testid="pfx-password"
                  />
                  <label className={`flex items-center justify-center gap-2 border border-dashed rounded-sm h-10 cursor-pointer transition-colors ${pfxBusy ? "opacity-50 pointer-events-none" : "border-zinc-300 hover:border-zinc-900 hover:bg-white"}`} data-testid="pfx-upload-label">
                    <Upload className="w-4 h-4" />
                    <span className="text-xs font-mono uppercase tracking-wider">{pfxBusy ? "Lendo..." : "Selecionar .pfx"}</span>
                    <input type="file" accept=".pfx,.p12,.PFX,.P12" className="hidden" onChange={onPfxChange} data-testid="pfx-upload-input" />
                  </label>
                </div>
                {form.cert_info?.common_name && (
                  <div className="mt-3 p-3 bg-white border border-zinc-200 rounded-sm text-xs space-y-1" data-testid="pfx-info">
                    <div className="flex items-center gap-2 font-semibold">
                      {form.cert_info.expired
                        ? <AlertCircle className="w-3.5 h-3.5 text-red-600" />
                        : <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />}
                      <span>{form.cert_info.common_name}</span>
                    </div>
                    {form.cert_info.cnpj && <div className="font-mono text-zinc-600">CNPJ: {form.cert_info.cnpj}</div>}
                    <div className="text-zinc-600">Emissor: {form.cert_info.issuer || "—"}</div>
                    <div className={`font-mono ${form.cert_info.expired ? "text-red-600" : "text-zinc-600"}`}>
                      Válido até {form.cert_info.valid_to?.slice(0, 10)} ({form.cert_info.days_to_expire} dias)
                    </div>
                  </div>
                )}
              </div>

              <Field label="Certificado (PEM)" value={form.certificado} onChange={update("certificado")} mono className="sm:col-span-2" type="password" testid="banco-cert" />
              <Field label="Chave Privada (PEM)" value={form.chave_privada} onChange={update("chave_privada")} mono className="sm:col-span-2" type="password" testid="banco-privkey" />
              <Field label="Webhook URL (banco → hub)" value={form.webhook_url} onChange={update("webhook_url")} className="sm:col-span-2" testid="banco-webhook" />
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">Ambiente</Label>
                <Select value={form.ambiente} onValueChange={(v) => setForm((f) => ({ ...f, ambiente: v }))}>
                  <SelectTrigger className="rounded-sm h-10" data-testid="banco-ambiente"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sandbox">Sandbox (mock)</SelectItem>
                    <SelectItem value="homologacao">Homologação</SelectItem>
                    <SelectItem value="producao">Produção</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3 mt-6">
                <Switch checked={form.ativo} onCheckedChange={(v) => setForm((f) => ({ ...f, ativo: v }))} data-testid="banco-ativo" />
                <span className="text-sm">Ativo</span>
              </div>

              {/* AVANÇADO: BOLETO + COBRANÇA */}
              <details className="sm:col-span-2 border border-zinc-200 rounded-sm" data-testid="advanced-block">
                <summary className="cursor-pointer px-4 py-2 text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-600 hover:bg-zinc-50">
                  Avançado: Boleto e cobrança
                </summary>
                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 bg-zinc-50">
                  <p className="text-xs text-zinc-500 sm:col-span-2">
                    Use estes campos para registro, consulta e cobrança de boletos. Eles ficam separados do monitoramento Pix para evitar mistura de endpoints.
                  </p>
                  <Field label="OAuth Token URL" value={form.oauth_url} onChange={update("oauth_url")} mono className="sm:col-span-2" testid="adv-oauth-url" />
                  <Field label="API Base URL" value={form.api_base_url} onChange={update("api_base_url")} mono className="sm:col-span-2" testid="adv-api-base" />
                  <Field label="OAuth Scope" value={form.oauth_scope} onChange={update("oauth_scope")} mono className="sm:col-span-2" testid="adv-oauth-scope" />
                  {form.bank_code === "001" && (
                    <Field
                      label="BB gw-dev-app-key"
                      value={form.extra_headers?.["gw-dev-app-key"] || ""}
                      onChange={updateExtra("gw-dev-app-key")}
                      mono
                      className="sm:col-span-2"
                      testid="adv-bb-app-key"
                    />
                  )}
                  {form.bank_code === "237" && (
                    <>
                      <Field
                        label="Bradesco CNPJ beneficiário (opcional)"
                        value={form.extra_headers?.beneficiario_cnpj || ""}
                        onChange={updateExtra("beneficiario_cnpj")}
                        mono
                        className="sm:col-span-2"
                        testid="adv-bradesco-beneficiario-cnpj"
                        placeholder="Vazio usa o CNPJ do emitente/certificado."
                      />
                      <Field
                        label="Bradesco nuNegociacao (se diferente do convênio)"
                        value={form.extra_headers?.nuNegociacao || ""}
                        onChange={updateExtra("nuNegociacao")}
                        mono
                        className="sm:col-span-2"
                        testid="adv-bradesco-nu-negociacao"
                        placeholder="Número de negociação da API Cobrança."
                      />
                      <Field
                        label="Bradesco idProduto/carteira (opcional)"
                        value={form.extra_headers?.idProduto || ""}
                        onChange={updateExtra("idProduto")}
                        mono
                        className="sm:col-span-2"
                        testid="adv-bradesco-id-produto"
                        placeholder="Ex.: 09"
                      />
                      <Field
                        label="Bradesco kid (opcional)"
                        value={form.extra_headers?.kid || ""}
                        onChange={updateExtra("kid")}
                        mono
                        className="sm:col-span-2"
                        testid="adv-bradesco-kid"
                      />
                      <Field
                        label="Bradesco JWT audience (opcional)"
                        value={form.extra_headers?.jwt_audience || ""}
                        onChange={updateExtra("jwt_audience")}
                        mono
                        className="sm:col-span-2"
                        testid="adv-bradesco-jwt-audience"
                        placeholder="Vazio usa a URL de token configurada."
                      />
                      <Field
                        label="Bradesco JWT ver (opcional)"
                        value={form.extra_headers?.jwt_ver || ""}
                        onChange={updateExtra("jwt_ver")}
                        mono
                        className="sm:col-span-2"
                        testid="adv-bradesco-jwt-ver"
                        placeholder="Preencha 1.1 apenas se o portal/documentação exigir."
                      />
                      <SecretTextarea
                        label="Bradesco chave JWS (.key PEM)"
                        value={form.extra_headers?.jws_private_key || ""}
                        onChange={updateExtra("jws_private_key")}
                        className="sm:col-span-2"
                        testid="adv-bradesco-jws-key"
                        placeholder="Cole aqui a chave RSA privada cujo par publico foi cadastrado no portal Bradesco."
                      />
                    </>
                  )}
                </div>
              </details>
              {pixModuleEnabled ? (
                <details className="sm:col-span-2 border border-emerald-200 rounded-sm" data-testid="pix-monitoring-block">
                  <summary className="cursor-pointer px-4 py-2 text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-700 hover:bg-emerald-50 flex items-center gap-2">
                    <Radio className="w-3.5 h-3.5" /> Avançado: Monitoramento Pix
                  </summary>
                  <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 bg-emerald-50/40">
                    <p className="text-xs text-zinc-600 sm:col-span-2">
                      Exibido porque o módulo Controle MeuPix está ativo na Administração. Use estes campos somente para consulta/monitoramento Pix por banco.
                    </p>
                    <Field label="URL base Pix / monitoramento" value={form.pix_monitoring_url || ""} onChange={update("pix_monitoring_url")} mono className="sm:col-span-2" testid="adv-pix-monitoring-url" />
                    <Field label="Token URL Pix" value={form.pix_oauth_url || ""} onChange={update("pix_oauth_url")} mono className="sm:col-span-2" testid="adv-pix-oauth-url" />
                    <Field label="Webhook Pix (banco → hub)" value={form.pix_webhook_url || ""} onChange={update("pix_webhook_url")} mono className="sm:col-span-2" testid="adv-pix-webhook-url" placeholder={suggestedPixWebhookUrl} />
                    <Field label="OAuth Scope Pix" value={form.pix_oauth_scope || ""} onChange={update("pix_oauth_scope")} mono className="sm:col-span-2" testid="adv-pix-oauth-scope" />
                    {suggestedPixWebhookUrl && (
                      <div className="sm:col-span-2 rounded-sm border border-emerald-200 bg-white p-3 text-xs text-zinc-600">
                        <div className="font-semibold text-zinc-700 mb-1">URL sugerida para cadastrar no banco</div>
                        <div className="font-mono break-all">{suggestedPixWebhookUrl}</div>
                      </div>
                    )}
                  </div>
                </details>
              ) : (
                <div className="sm:col-span-2 rounded-sm border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-500" data-testid="pix-monitoring-disabled">
                  Monitoramento Pix oculto: ative o módulo Controle MeuPix na Administração para configurar URLs, webhook e scope Pix por banco.
                </div>
              )}
              <DialogFooter className="sm:col-span-2 mt-2">
                <Button type="submit" className="rounded-sm" data-testid="banco-submit">{editId ? "Salvar" : "Cadastrar"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="rounded-sm border border-zinc-200 shadow-none overflow-x-auto">
        <table className="tbl" data-testid="bancos-table">
          <thead>
            <tr>
              <th>Banco</th><th>Agência</th><th>Conta</th><th>Convênio</th><th>Ambiente</th><th>Status</th><th className="w-24 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-sm text-zinc-500">Nenhum banco configurado.</td></tr>}
            {items.map((b) => (
              <tr key={b.id} data-testid={`banco-row-${b.id}`}>
                <td className="font-medium">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-zinc-400" /> {b.bank_code} – {b.banco_nome}
                  </div>
                </td>
                <td className="font-mono text-xs">{b.agencia}</td>
                <td className="font-mono text-xs">{b.conta}</td>
                <td className="font-mono text-xs">{b.convenio || "-"}</td>
                <td><span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border rounded-sm">{formatEnvironmentLabel(b.ambiente)}</span></td>
                <td>{b.ativo
                  ? <span className="text-[10px] font-mono uppercase text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-sm">Ativo</span>
                  : <span className="text-[10px] font-mono uppercase text-zinc-500 bg-zinc-100 border border-zinc-200 px-2 py-0.5 rounded-sm">Inativo</span>}
                </td>
                <td className="text-right">
                  <button onClick={() => onOnboard(b)} className="p-1.5 hover:bg-zinc-100 rounded-sm" data-testid={`banco-help-${b.id}`} title="Como obter credenciais">
                    <HelpCircle className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => testar(b)} className="p-1.5 hover:bg-zinc-100 rounded-sm" data-testid={`banco-test-${b.id}`} title="Testar conexão">
                    <Zap className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => edit(b)} className="p-1.5 hover:bg-zinc-100 rounded-sm" data-testid={`banco-edit-${b.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => del(b)} className="p-1.5 hover:bg-red-50 hover:text-red-600 rounded-sm" data-testid={`banco-delete-${b.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Onboarding dialog */}
      <Dialog open={!!onboard} onOpenChange={(o) => { if (!o) setOnboard(null); }}>
        <DialogContent className="max-w-xl rounded-sm w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="onboarding-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <HelpCircle className="w-5 h-5" /> Como conectar ao {onboard?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">Passo a passo</div>
            <ol className="space-y-3 pl-4 list-decimal text-sm">
              {onboard?.steps?.map((s, i) => <li key={i}>{s}</li>)}
            </ol>
            {onboard?.requires_cert && (
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-900">
                <b>Atenção:</b> este banco exige certificado digital (mTLS / ICP-Brasil). Sem certificado, a integração só funciona em modo sandbox/mock.
              </div>
            )}
            <a href={onboard?.portal} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-zinc-900 underline" data-testid="onboarding-portal-link">
              Portal do desenvolvedor <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOnboard(null)} data-testid="onboarding-close">Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!testDebug} onOpenChange={(o) => { if (!o) setTestDebug(null); }}>
        <DialogContent className="max-w-3xl rounded-sm w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="bank-test-debug-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <AlertCircle className="w-5 h-5" /> Diagnóstico da conexão bancária
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-zinc-700">{testDebug?.message}</div>
            <pre className="max-h-[55vh] overflow-auto rounded-sm border border-zinc-200 bg-zinc-950 p-3 text-xs text-zinc-50">
{JSON.stringify(testDebug?.diagnostics || {}, null, 2)}
            </pre>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              className="rounded-sm"
              onClick={async () => {
                await navigator.clipboard.writeText(JSON.stringify(testDebug?.diagnostics || {}, null, 2));
                toast.success("JSON copiado");
              }}
              data-testid="bank-test-debug-copy"
            >
              <Copy className="w-4 h-4 mr-2" /> Copiar JSON
            </Button>
            <Button variant="outline" className="rounded-sm" onClick={() => setTestDebug(null)} data-testid="bank-test-debug-close">Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, mono, className = "", testid, ...props }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">{label}</Label>
      <Input {...props} className={`rounded-sm h-10 ${mono ? "font-mono text-xs" : ""}`} data-testid={testid} />
    </div>
  );
}

function SecretTextarea({ label, className = "", testid, ...props }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">{label}</Label>
      <Textarea {...props} rows={4} className="rounded-sm font-mono text-xs" data-testid={testid} />
    </div>
  );
}
